import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ControlPasswordProtected from '../components/ControlPasswordProtected';
import {
    IconButton, Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, TextField, Button, Checkbox, FormControlLabel, Paper, Grid, Slider,
    Switch, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { 
    OpenInNew as OpenInNewIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon, 
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,

} from '@mui/icons-material';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import SaveIcon from '@mui/icons-material/Save';
import API_BASE_URL from '../config';
import './Presets.css';

const PresetDetailsContent = () => {
    const { presetId } = useParams();
    const [preset, setPreset] = useState(null);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
  
    const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
    const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
    const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
    const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');
  
    const [openDialog, setOpenDialog] = useState(false);
    const [newPresetName, setNewPresetName] = useState('');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [displayEnabled, setDisplayEnabled] = useState(true);
    const [brightnessEnabled, setBrightnessEnabled] = useState(true);
    const [display, setDisplay] = useState({
        flash: 0,
        regular_content: 'S:not-found.png',
        speeding_content: 'S:not-found.png'
    });
    const [brightness, setBrightness] = useState([Array(24).fill(0)]);

    const [mediaImages, setMediaImages] = useState({});
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [imageDialogType, setImageDialogType] = useState('');
    const [selectedImage, setSelectedImage] = useState('');
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [userMediaImages, setUserMediaImages] = useState({});

    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [openWarningDialog, setOpenWarningDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setSnackbar({ ...snackbar, open: false });
      };
  
    const fetchPresetDetails = useCallback(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/device/preset/${presetId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.status === 401) {
            navigate('/unauthorized');
            return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch preset details');
        }
        const data = await response.json();
        setPreset(data);
        
        if (data.flash === -1 || data.regular_content === -1 || data.speeding_content === -1) {
            setDisplay({
                flash: 0,
                regular_content: 'S:not-found.png',
                speeding_content: 'S:not-found.png'
            });
            // todo: setDisplayEnabled(false);
        } else {
            setDisplay({
                flash: data.flash,
                regular_content: data.regular_content,
                speeding_content: data.speeding_content
            });
            setDisplayEnabled(true);
        }

        if (data.brightness.length === 24) {
            setBrightness(data.brightness);
            setBrightnessEnabled(true);
        } else {
            setBrightness(Array(24).fill(0));
            // todo: setBrightnessEnabled(false);            
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, [presetId]);
  
    useEffect(() => {
      fetchPresetDetails();
    }, [fetchPresetDetails]);
  
    useEffect(() => {
      const checkLocalStorage = () => {
        const storedPrimaryColor = localStorage.getItem('primaryColor');
        const storedSecondaryColor = localStorage.getItem('secondaryColor');
        const storedDarkMode = localStorage.getItem('darkMode') === 'true';
  
        if (storedPrimaryColor !== primaryColor) {
          setPrimaryColor(storedPrimaryColor);
        }
        if (storedSecondaryColor !== secondaryColor) {
          setSecondaryColor(storedSecondaryColor);
        }
        if (storedDarkMode !== darkMode) {
          setDarkMode(storedDarkMode);
          setTextColor(storedDarkMode ? '#ffffff' : '#000000');
        }
      };
  
      checkLocalStorage();
      const intervalId = setInterval(checkLocalStorage, 200);
  
      return () => clearInterval(intervalId);
    }, [primaryColor, secondaryColor, darkMode]);
  
    const handleOpenDialog = () => {
      setNewPresetName(preset.preset_name);
      setOpenDialog(true);
    };
  
    const handleCloseDialog = () => {
      setOpenDialog(false);
    };

    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
    };
      
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleDeletePreset = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/device/preset/${presetId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
      
          if (!response.ok) {
            throw new Error('Failed to delete preset');
          }
      
          handleCloseDeleteDialog();
          navigate('/presets');
        } catch (error) {
          console.error('Error deleting preset:', error);
        }
      };
  
    const handleUpdatePresetName = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/device/preset/${presetId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ preset_name: newPresetName })
        });
  
        if (!response.ok) {
          throw new Error('Failed to update preset name');
        }
  
        setPreset({ ...preset, preset_name: newPresetName });
        handleCloseDialog();
      } catch (error) {
        console.error('Error updating preset name:', error);
      }
    };

    const handleBackToPresets = () => {
        navigate('/presets');
    };

    const handleDisplayChange = (event) => {
        setDisplayEnabled(event.target.checked);
    };

    const handleDisplayUpdate = useCallback((property, value) => {
        setDisplay(prev => ({
          ...prev,
          [property]: value
        }));
      }, []);
    
    const handleBrightnessChange = (event) => {
        setBrightnessEnabled(event.target.checked);
    };

    const indicatorStyle = {
        marginLeft: '10px',
        fontSize: '0.875rem',
        fontWeight: 'bold',
    };

    const BrightnessControl = ({ brightness, onChange, isLocked }) => {
        const [localBrightness, setLocalBrightness] = useState(() => {
          return Array.isArray(brightness) && brightness.length === 24
            ? brightness
            : Array(24).fill(0);
        });
      
        useEffect(() => {
          const newBrightness = Array.isArray(brightness) && brightness.length === 24
            ? brightness
            : Array(24).fill(0);
          setLocalBrightness(newBrightness);
        }, [brightness]);
      
        const handleBrightnessChange = useCallback((index) => (event, newValue) => {
          setLocalBrightness(prev => {
            const newBrightness = [...prev];
            newBrightness[index] = newValue;
            return newBrightness;
          });
        }, []);
      
        const handleChangeCommitted = useCallback(() => {
          if (!isLocked) {
            onChange(localBrightness);
          }
        }, [isLocked, onChange, localBrightness]);
      
        return (
          <Box sx={{ display: 'flex' }}>
            {[0, 1].map((column) => (
              <Box key={column} sx={{ flex: 1, pr: column === 0 ? 2 : 0, pl: column === 1 ? 2 : 0 }}>
                {localBrightness.slice(column * 12, (column + 1) * 12).map((value, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 35 }}>
                      {`${(column * 12 + index).toString().padStart(2, '0')}:00`}
                    </Typography>
                    <Slider
                      size="small"
                      value={value}
                      onChange={handleBrightnessChange(column * 12 + index)}
                      onChangeCommitted={handleChangeCommitted}
                      aria-labelledby={`brightness-slider-${column * 12 + index}`}
                      valueLabelDisplay="auto"
                      disabled={isLocked}
                      step={10}
                      marks
                      min={0}
                      max={100}
                      sx={{ 
                        mx: 1,
                        '& .MuiSlider-thumb': {
                          width: 12,
                          height: 12,
                          color: isLocked ? 'grey' : 'primary.main',
                        },
                        '& .MuiSlider-track': {
                          color: isLocked ? 'grey' : 'primary.main',
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ minWidth: 25 }}>
                      {value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        );
    };

    const ContentPreview = ({ 
        contentMode, 
        regularContent, 
        speedingContent, 
        imageDisplayDuration, 
        speedDisplayDuration, 
        speedLimit,
        isRegular,
        flash,
        flashOnDuration,
        flashOffDuration
      }) => {
        const [currentDisplay, setCurrentDisplay] = useState('image');
        const [isFlashOn, setIsFlashOn] = useState(true);
        const [elapsedSwitchTime, setElapsedSwitchTime] = useState(0);
        const [elapsedFlashTime, setElapsedFlashTime] = useState(0);
      
        // Ensure minimum durations
        const safeImageDisplayDuration = Math.max(imageDisplayDuration, 1) * 1000;
        const safeSpeedDisplayDuration = Math.max(speedDisplayDuration, 1) * 1000;
        const safeFlashOnDuration = Math.max(flashOnDuration, 0.1) * 1000;
        const safeFlashOffDuration = Math.max(flashOffDuration, 0.1) * 1000;
      
        useEffect(() => {
          const interval = setInterval(() => {
            setElapsedSwitchTime(prev => prev + 100);
            setElapsedFlashTime(prev => prev + 100);
          }, 100);
      
          return () => clearInterval(interval);
        }, []);
      
        useEffect(() => {
          // Handle switching between image and speed
          if (contentMode === 3) { // Both mode
            if (currentDisplay === 'image' && elapsedSwitchTime >= safeImageDisplayDuration) {
              setCurrentDisplay('speed');
              setElapsedSwitchTime(0);
            } else if (currentDisplay === 'speed' && elapsedSwitchTime >= safeSpeedDisplayDuration) {
              setCurrentDisplay('image');
              setElapsedSwitchTime(0);
            }
          }
      
          // Handle flash effect
          if (flash) {
            if (isFlashOn && elapsedFlashTime >= safeFlashOnDuration) {
              setIsFlashOn(false);
              setElapsedFlashTime(0);
            } else if (!isFlashOn && elapsedFlashTime >= safeFlashOffDuration) {
              setIsFlashOn(true);
              setElapsedFlashTime(0);
            }
          }
        }, [contentMode, currentDisplay, elapsedSwitchTime, elapsedFlashTime, safeImageDisplayDuration, safeSpeedDisplayDuration, flash, isFlashOn, safeFlashOnDuration, safeFlashOffDuration]);
      
        const renderSpeedDisplay = () => (
          <Box 
            sx={{ 
              width: '100%', 
              height: '100%', 
              backgroundColor: 'black', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center'
            }}
          >
            <Typography 
              variant="h1" 
              sx={{ color: isRegular ? 'green' : 'red' }}
            >
              {speedLimit}
            </Typography>
          </Box>
        );
      
        const renderContent = () => {
          if (flash && !isFlashOn) {
            return <Box sx={{ width: '100%', height: '100%', backgroundColor: 'black' }} />;
          }
      
          if (contentMode === 3) { // Both mode
            return currentDisplay === 'image' 
              ? <img src={isRegular ? regularContent : speedingContent} alt="Content" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : renderSpeedDisplay();
          } else if (contentMode === 2) { // Speed only
            return renderSpeedDisplay();
          } else { // Image only or default
            return <img src={isRegular ? regularContent : speedingContent} alt="Content" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />;
          }
        };
      
        return (
          <Box sx={{ width: '200px', height: '150px', border: '1px solid #ccc' }}>
            {renderContent()}
          </Box>
        );
    };

    const fetchMediaImage = useCallback(async (mediaName) => {
        try {
            const response = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(mediaName)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.status === 401) {
              navigate('/unauthorized');
              return;
            }
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error(`Error fetching media image for ${mediaName}:`, error);
            return null;
        }
    }, []);

    const handleOpenImageDialog = (type) => {
        setImageDialogType(type);
        setOpenImageDialog(true);
        fetchUserMediaList();
      };
    
      const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
        setSelectedImage('');
      };
    
      const handleImageSelect = (imageName) => {
        setSelectedImage(imageName);
        if (imageName) {
          if (imageDialogType === 'regular') {
            handleDisplayUpdate('regular_content', imageName);
          } else if (imageDialogType === 'speeding') {
            handleDisplayUpdate('speeding_content', imageName);
          }
        }
        handleCloseImageDialog();
      };

    useEffect(() => {
        const fetchImages = async () => {
          const newMediaImages = {};
          if (display) {
            const regularContentName = display.regular_content;
            const speedingContentName = display.speeding_content;
            if (regularContentName) {
                newMediaImages[regularContentName] = await fetchMediaImage(regularContentName);
            }
            if (speedingContentName) {
                newMediaImages[speedingContentName] = await fetchMediaImage(speedingContentName);
            }
          }
          setMediaImages(newMediaImages);
        };
    
        if (preset && display) {
          fetchImages();
        }
    }, [preset, display, fetchMediaImage]);

    const fetchUserMediaList = useCallback(async () => {
        setIsLoadingImages(true);
        try {
          const response = await fetch(`${API_BASE_URL}/device/media-list`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
    
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
    
          const mediaFiles = await response.json();
          const imagePromises = mediaFiles.map(async (file) => {
            const imageResponse = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(file.file_name)}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (!imageResponse.ok) {
              console.error(`Failed to fetch image for ${file.file_name}`);
              return null;
            }
            const blob = await imageResponse.blob();
            return { name: file.file_name, url: URL.createObjectURL(blob) };
          });
    
          const images = await Promise.all(imagePromises);
          const imageMap = images.reduce((acc, img) => {
            if (img) {
              acc[img.name] = img.url;
            }
            return acc;
          }, {});
          
          setUserMediaImages(imageMap);
        } catch (error) {
          console.error('Error fetching user media images:', error);
        } finally {
          setIsLoadingImages(false);
        }
      }, []);


    const handleSavePreset = () => {
        if (displayEnabled && (display.regular_content === 'S:not-found.png' || display.speeding_content === 'S:not-found.png')) {
            setErrorMessage('Please select images for the display or unselect display if you don\'t want to include it in the preset.');
            setOpenErrorDialog(true);
            return;
        }

        setOpenWarningDialog(true);
    };

    const handleConfirmSave = async () => { // TODO
        if (!displayEnabled) {
            setDisplay({
                flash: -1,
                regular_content: '-1',
                speeding_content: '-1'
            })
        }

        if (!brightnessEnabled) {
            setBrightness([]);
        }

        try {
            const response = await fetch(`${API_BASE_URL}/device/preset-params/${presetId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ display, brightness })
            });

            if (response.status === 401) {
              navigate('/unauthorized');
              return;
            }
      
            if (response.status === 200) {
              setSnackbar({
                open: true,
                message: 'Preset updated successfully',
                severity: 'success'
              });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to update preset',
                    severity: 'error'
                  });
              throw new Error('Failed to update preset');
            }
        } catch (error) {
            console.error('Error updating preset:', error);
            // Show error message to the user
            // TODO
        } finally {
            setOpenWarningDialog(false);
        }
      };
  
    if (loading) return <Typography style={{ color: primaryColor }}>Loading...</Typography>;
    if (error) return <Typography style={{ color: primaryColor }}>Error: {error}</Typography>;
    if (!preset) return <Typography style={{ color: primaryColor }}>No preset found</Typography>;
  
    return (
        <div className={`preset-details ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '0px', backgroundColor: primaryColor }}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={snackbar.open}
                autoHideDuration={10000}
                onClose={handleSnackbarClose}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                {snackbar.message}
                </Alert>
            </Snackbar>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToPresets}
            style={{ marginRight: '20px', color: textColor }}
            >
            Back to Presets
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h4" style={{ color: textColor, marginRight: '10px' }}>{preset.preset_name}</Typography>
            <IconButton onClick={handleOpenDialog} style={{ color: textColor }}>
                <EditIcon />
            </IconButton>
            <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSavePreset}
                sx={{
                marginLeft: '10px',
                color: 'green !important',
                borderColor: 'green !important',
                '&:hover': {
                    backgroundColor: darkMode ? 'rgba(26, 216, 26, 0.1) !important' : 'rgba(26, 216, 26, 0.05) !important',
                    borderColor: 'green !important',
                },
                }}
            >
                Save Preset
            </Button>
            <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleOpenDeleteDialog}
                sx={{
                marginLeft: '10px',
                color: 'red !important',
                borderColor: 'red !important',
                '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255, 0, 0, 0.1) !important' : 'rgba(255, 0, 0, 0.05) !important',
                    borderColor: 'red !important',
                },
                }}
            >
                Delete Preset
            </Button>
            </Box>
            <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              backgroundColor: displayEnabled ? secondaryColor : primaryColor,
              color: textColor,
              transition: 'background-color 0.3s'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Display</Typography>
              {
                /*
                <Checkbox 
                checked={displayEnabled}
                disabled
                onChange={handleDisplayChange}
                sx={{
                  color: displayEnabled ? 'green' : textColor,
                  '&.Mui-checked': {
                    color: 'green',
                  },
                }}
              />
                */
              }
            </Box>
            {displayEnabled && (
                <>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={display?.flash === 1}
                        onChange={(event) => handleDisplayUpdate('flash', event.target.checked ? 1 : 0)}
                        />
                    }
                    label={<Typography sx={{ color: textColor }}>Flash</Typography>}
                    />
                    <Grid container spacing={4} sx={{ mt: 2, justifyContent: 'center' }}>
                    <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Regular Preview</Typography>
                        {display === null ? (
                            <CircularProgress size={40} />
                        ) : display?.regular_content ? (
                            <ContentPreview 
                                contentMode={1}
                                regularContent={mediaImages[display.regular_content]}
                                speedingContent={mediaImages[display.speeding_content]}
                                imageDisplayDuration={1}
                                speedDisplayDuration={1}
                                speedLimit={60}
                                isRegular={true}
                                flash={display.flash === 1}
                                flashOnDuration={0.4}
                                flashOffDuration={0.4}
                            />
                        ) : (
                            <SyncProblemIcon color="error" style={{ fontSize: 40 }} />
                        )}
                        </Box>
                        <Button 
                        fullWidth
                        variant="contained" 
                        onClick={() => handleOpenImageDialog('regular')}
                        sx={{
                            color: textColor,
                            backgroundColor: primaryColor,
                            '&:hover': {
                            backgroundColor: secondaryColor,
                            },
                            padding: '10px 20px',
                            borderRadius: '5px',
                        }}
                        >
                        Update Regular Image
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Speeding Preview</Typography>
                        {display === null ? (
                            <CircularProgress size={40} />
                        ) : display?.speeding_content ? (
                            <ContentPreview 
                                contentMode={1}
                                regularContent={mediaImages[display.regular_content]}
                                speedingContent={mediaImages[display.speeding_content]}
                                imageDisplayDuration={1}
                                speedDisplayDuration={1}
                                speedLimit={60}
                                isRegular={false}
                                flash={display.flash === 1}
                                flashOnDuration={0.4}
                                flashOffDuration={0.4}
                            />
                        ) : (
                            <SyncProblemIcon color="error" style={{ fontSize: 40 }} />
                        )}
                        </Box>
                        <Button 
                        fullWidth
                        variant="contained" 
                        onClick={() => handleOpenImageDialog('speeding')}
                        sx={{
                            color: textColor,
                            backgroundColor: primaryColor,
                            '&:hover': {
                            backgroundColor: secondaryColor,
                            },
                            padding: '10px 20px',
                            borderRadius: '5px',
                        }}
                        >
                        Update Speeding Image
                        </Button>
                    </Grid>
                    </Grid>
                </>
                )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              backgroundColor: brightnessEnabled ? secondaryColor : primaryColor,
              color: textColor,
              transition: 'background-color 0.3s'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Brightness</Typography>
              {
                /*
                <Checkbox 
                  checked={brightnessEnabled}
                  onChange={handleBrightnessChange}
                  sx={{
                    color: brightnessEnabled ? 'green' : textColor,
                    '&.Mui-checked': {
                      color: 'green',
                    },
                  }}
                />
                */
              }
            </Box>
            {brightnessEnabled && (
              <Typography variant="h6">
                <Typography 
                component="span" 
                style={{ 
                    ...indicatorStyle,
                }}
                >
                </Typography>
                <BrightnessControl 
                brightness={brightness}
                onChange={(newBrightness) => {
                setBrightness(newBrightness);
                }}
            />
            </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog}
            PaperProps={{
                style: {
                  backgroundColor: darkMode ? '#333' : '#fff',
                  color: textColor,
                },
            }}
          >
            <DialogTitle>Edit Preset Name</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Preset Name"
                type="text"
                fullWidth
                value={newPresetName}
                inputProps={{ maxLength: 255 }}
                onChange={(e) => setNewPresetName(e.target.value)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: textColor,
                    },
                    '& .MuiInputBase-input': {
                      color: textColor,
                    },
                    '& .MuiFormHelperText-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                    },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} sx={{ color: textColor }}>Cancel</Button>
              <Button 
                onClick={handleUpdatePresetName}
                sx={{
                    color: !newPresetName
                      ? (darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)') 
                      : (darkMode ? '#4caf50' : '#45a049'),
                    backgroundColor: !newPresetName
                      ? 'transparent'
                      : (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)'), 
                    '&:hover': {
                      backgroundColor: !newPresetName
                        ? 'transparent'
                        : (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)'),
                    },
                }}
                disabled={!newPresetName}
                >
                Update
              </Button>
            </DialogActions>
          </Dialog>
        <Dialog 
            open={openDeleteDialog} 
            onClose={handleCloseDeleteDialog}
            PaperProps={{
                style: {
                backgroundColor: darkMode ? '#333' : '#fff',
                color: textColor,
                },
            }}
            >
            <DialogTitle>Delete Preset</DialogTitle>
            <DialogContent>
                <DialogContentText style={{ color: textColor }}>
                Are you sure you want to delete this preset? This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDeleteDialog} sx={{ color: textColor }}>Cancel</Button>
                <Button 
                onClick={handleDeletePreset}
                sx={{
                    color: 'red',
                    '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.05)',
                    },
                }}
                >
                Delete
                </Button>
            </DialogActions>
            </Dialog>
            <Dialog 
                open={openImageDialog} 
                onClose={handleCloseImageDialog}
                PaperProps={{
                style: {
                    backgroundColor: primaryColor,
                    color: textColor,
                },
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ color: textColor }}>
                Select {imageDialogType === 'regular' ? 'Regular' : 'Speeding'} Image
                </DialogTitle>
                <DialogContent>
                {isLoadingImages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                    {Object.entries(userMediaImages).map(([fileName, blobUrl]) => (
                        <Grid item xs={12} sm={6} key={fileName}>
                        <Paper 
                            onClick={() => handleImageSelect(fileName)}
                            sx={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedImage === fileName ? 'green' : secondaryColor,
                            '&:hover': {
                                backgroundColor: selectedImage === fileName ? 'darkgreen' : 'rgba(255, 255, 255, 0.1)',
                            },
                            p: 2,
                            }}
                        >
                            <Grid container spacing={6} alignItems="center">
                            <Grid item xs={6}>
                                <Box
                                sx={{
                                    width: '100%',
                                    paddingTop: '75%',
                                    position: 'relative',
                                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                }}
                                >
                                <Box
                                    sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    }}
                                >
                                    <img
                                    src={blobUrl}
                                    alt={fileName}
                                    style={{ 
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        aspectRatio: '64 / 48',
                                    }}
                                    />
                                </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" sx={{ color: textColor, wordBreak: 'break-word' }}>
                                {fileName}
                                </Typography>
                            </Grid>
                            </Grid>
                        </Paper>
                        </Grid>
                    ))}
                    </Grid>
                )}
                </DialogContent>
                <DialogActions>
                <Button onClick={handleCloseImageDialog} sx={{ color: textColor }}>Cancel</Button>
                </DialogActions>
            </Dialog>
            {/* Error Dialog */}
                <Dialog
                open={openErrorDialog}
                onClose={() => setOpenErrorDialog(false)}
                PaperProps={{
                    style: {
                        backgroundColor: primaryColor,
                        color: `${textColor}`,
                    },
                }}
                aria-labelledby="error-dialog-title"
                aria-describedby="error-dialog-description"
                >
                <DialogTitle id="error-dialog-title">{"Error"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="error-dialog-description" style={{ color: textColor }}>
                    {errorMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenErrorDialog(false)} color="primary">
                    OK
                    </Button>
                </DialogActions>
                </Dialog>

                {/* Warning Dialog */}
            <Dialog
                open={openWarningDialog}
                onClose={() => setOpenWarningDialog(false)}
                PaperProps={{
                    style: {
                        backgroundColor: primaryColor,
                        color: textColor,
                    },
                }}
                aria-labelledby="warning-dialog-title"
                aria-describedby="warning-dialog-description"
                >
                <DialogTitle id="warning-dialog-title">{"Warning"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="warning-dialog-description" style={{ color: textColor }}>
                        The previous preset will be reset. Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenWarningDialog(false)} color="primary">
                    Cancel
                    </Button>
                    <Button onClick={handleConfirmSave} color="primary" autoFocus>
                    Confirm
                    </Button>
                </DialogActions>
                </Dialog>
        </div>
    );
};

const PresetDetails = () => {
  // const ProtectedComponent = ControlPasswordProtected(PresetDetailsContent);
  // return <ProtectedComponent />;
  return <PresetDetailsContent />;
};

export default PresetDetails;